// Small shared UI helpers
import { useEffect, useState } from "react";
import api from "../lib/api";

// Maps the active tenant plan to the product brand shown in the page header.
const brandFromPlan = (plan) => {
  if (!plan) return "ClaroShield";
  if (String(plan).startsWith("shield")) return "ClaroShield";
  return "ClaroShield Light"; // free_trial and anything else on the light tier
};

let _planBrandCache = null;

export function PageHeader({ title, subtitle, action }) {
  const [brand, setBrand] = useState(_planBrandCache || "ClaroShield");
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/tenant/me");
        _planBrandCache = brandFromPlan(data?.plan);
        if (alive) setBrand(_planBrandCache);
      } catch { /* non-blocking chrome — keep last known brand */ }
    };
    load();
    const h = () => load();
    window.addEventListener("claro:tenant-updated", h);
    window.addEventListener("claro:registrations-changed", h);
    return () => {
      alive = false;
      window.removeEventListener("claro:tenant-updated", h);
      window.removeEventListener("claro:registrations-changed", h);
    };
  }, []);
  return (
    <div className="flex items-start justify-between border-b border-border px-8 py-6 bg-card sticky top-0 z-10">
      <div>
        <div className="overline text-muted-foreground mb-1" data-testid="page-header-brand">{brand}</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground border-border",
  review: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/40",
  changes_requested: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/40",
  approved: "bg-green-50 text-green-700 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40",
  published: "bg-primary text-primary-foreground border-black",
  active: "bg-green-50 text-green-700 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40",
  open: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/40",
  in_progress: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/40",
  done: "bg-green-50 text-green-700 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40",
};

export function StatusBadge({ status, ...rest }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground border-border";
  return (
    <span {...rest} className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}>
      {String(status || "").replace(/_/g, " ")}
    </span>
  );
}

const SEV = {
  high: "bg-red-50 text-red-700 border-red-300",
  medium: "bg-amber-50 text-amber-700 border-amber-300",
  low: "bg-secondary text-gray-700 border-gray-300",
};
export function SeverityDot({ severity }) {
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase ${SEV[severity] || SEV.low}`}>{severity}</span>;
}

const ENFORCEMENT = {
  currently_enforceable: { cls: "bg-green-50 text-green-700 border-green-300", label: "Enforceable" },
  future_enforceable: { cls: "bg-amber-50 text-amber-800 border-amber-400", label: "Upcoming" },
  superseded: { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Superseded" },
  retired: { cls: "bg-secondary text-gray-500 border-gray-300", label: "Retired" },
};
export function EnforcementBadge({ status, effectiveDate, ...rest }) {
  const st = status || "currently_enforceable";
  const e = ENFORCEMENT[st] || ENFORCEMENT.currently_enforceable;
  const showDate = effectiveDate && ["currently_enforceable", "future_enforceable", "superseded"].includes(st);
  const datePrefix = st === "future_enforceable" ? ": Eff " : " · ";
  return (
    <span {...rest} className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${e.cls}`}>
      {e.label}{showDate ? `${datePrefix}${effectiveDate}` : ""}
    </span>
  );
}

const ACCESS_BADGES = {
  active:  { cls: "bg-green-50 text-green-700 border-green-300", label: "Active", icon: "circle" },
  future:  { cls: "bg-blue-50 text-blue-700 border-blue-300", label: "NERC Readiness", icon: "clock" },
  superseded: { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Superseded", icon: "archive" },
  retired: { cls: "bg-secondary text-gray-500 border-gray-300", label: "Retired", icon: "archive" },
  locked:  { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Outside scope", icon: "lock" },
};
// `access` is the resolver block from the backend ({state, badge, badge_label, enforceable_date, retirement_date, plan_locked}).
// Renders only when the workspace plan applies applicability-based lockout (plan_locked).
export function AccessBadge({ access, ...rest }) {
  if (!access || !access.plan_locked) return null;
  const key = access.badge || access.state || "active";
  const e = ACCESS_BADGES[key] || ACCESS_BADGES.active;
  let label = access.badge_label || e.label;
  if (key === "future" && access.enforceable_date) label += ` · ${access.enforceable_date}`;
  if (key === "retired" && access.retirement_date) label += ` · ${access.retirement_date}`;
  return (
    <span {...rest} title={key === "locked" ? "Outside your current Registration scope" : undefined}
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${e.cls}`}>
      {label}
    </span>
  );
}

export function ReservedBadge({ ...rest }) {
  return (
    <span {...rest} title="Reserved placeholder — no compliance obligation"
      className="inline-flex items-center gap-1 border border-dashed border-gray-300 bg-secondary text-gray-500 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide">
      Reserved
    </span>
  );
}

export function Empty({ title, hint }) {  return (
    <div className="border border-dashed border-border p-12 text-center bg-secondary/40">
      <div className="font-display font-bold text-lg">{title}</div>
      {hint && <div className="text-sm text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
